import { IWidget, TrendWidget } from '@griegconnect/krakentools-react-ui'
import { Widgets } from '../Types'
import { PassingIn } from '@app/common/ServicesWrapper/apis/PassingsApi'
import { GatePassings as PassingsIcon } from '@griegconnect/krakentools-react-icons'
import { DateTime, Duration, Interval } from 'luxon'
import { IChartData } from '@app/common/dashboard/LineChart'
import _ from 'lodash'
import LineChartWidget from '@app/common/dashboard/LineChartWidget'
import * as TimePeriod from '../../components/TimePeriod'
import { useEffect, useState } from 'react'
import { useServices } from '@app/common/ServicesWrapper'
import { useQuery } from '@tanstack/react-query'
import { WithActiveTenant } from '@app/routes/WithActiveTenant'
import * as SkeletonLoader from '../SkeletonLoader'
import { useTranslation } from "react-i18next"

const getMiddleDate = () => {
  return DateTime.now().minus({ day: 1 })
}

interface ITrendWidgetData {
  direction: 'up' | 'down'
  value: number
  change: number
}

interface IChartWidgetData {
  data: IChartData[]
  colors: string[]
}

interface IPassingsWidgetProps extends Widgets.InfoWithData<PassingIn>, WithActiveTenant {
  isSM: boolean
  groupings: Widgets.Grouping[]
}

export const Widget: React.VFC<IPassingsWidgetProps> = ({
  widgetConstructor,
  data,
  onRemove,
  onSettings,
  onDetails,
  editing,
  isSM,
  groupings,
  activeTenant,
}) => {
  const { services } = useServices()
  const { t } = useTranslation()

  const [trendData, setTrendData] = useState<ITrendWidgetData | undefined>(undefined)
  const [chartData, setChartData] = useState<IChartWidgetData | undefined>(undefined)

  const timeFrom = widgetConstructor.timeFrom
    ? Duration.fromISO((widgetConstructor.timeFrom as unknown) as string)
    : undefined
  const timeTo = widgetConstructor.timeTo
    ? Duration.fromISO((widgetConstructor.timeTo as unknown) as string)
    : undefined

  const { data: fetchedData } = useQuery<PassingIn | undefined>(
    ['fetchedDataPassings', activeTenant.ref, timeFrom, data],
    async () => {
      if (timeFrom) {
        const period = TimePeriod.getConstructorDates(timeFrom, widgetConstructor.currentPeriod, timeTo)
        const interval = Interval.fromDateTimes(period.fromDate, period.toDate)
        const result = await services.passingsApi.passingIn(activeTenant.ref, { precision: 'hour', interval })
        return result
      } else {
        return data
      }
    }
  )

  const getPassingsChart = (widgetConstructor: Widgets.Constructor) => {
    const arrayLength: number = 24
    const startDate = widgetConstructor ? getMiddleDate() : DateTime.now().set({ year: 2000 }).startOf('year')
    const entries =
      (data?.entries || [])
        .filter((c) => DateTime.fromISO(c.time) >= startDate)
        .filter((c) =>
          !!widgetConstructor.grouping
            ? (!c.classified && !!widgetConstructor.grouping.find((g) => g === 'Andre')) ||
              !!widgetConstructor.grouping.find((g) => g === c.classified)
            : true
        )
        .filter((c) =>
          widgetConstructor.facilities ? !!widgetConstructor.facilities.find((f) => f === c.facility.name) : true
        ) ?? []
    const classified = Object.entries(_.groupBy(entries, (c) => c.classified || 'Andre'))
    const chartData = [...classified.map(([name, values]) => ({ name, data: [...values] }))]
    for (const info of chartData) {
      info.data = info.data.filter(
        (info, index, array) =>
          index ===
          array.findIndex(
            (e) => e.time === info.time && e.facility.id === info.facility.id && e.classified === info.classified
          )
      )
    }
    const mergedData: IChartData[] = []
    const chartColors: string[] = []

    for (const info of chartData) {
      const grouping = groupings.find((g) => g.name === info.name)
      grouping?.color && chartColors.push(grouping.color)
      const byFacility = Object.entries(_.groupBy(info.data, (d) => d.facility.name))
      const dataArray: number[] = []
      for (const value of byFacility[0][1]) {
        const valueIndex = byFacility[0][1].indexOf(value)
        let newValue = 0
        for (const facility of byFacility) {
          newValue += facility[1][valueIndex].count
        }
        dataArray.push(newValue)
      }
      while (dataArray.length < arrayLength) {
        dataArray.push(0)
      }
      mergedData.push({ name: info.name, data: dataArray })
    }

    const filledData = mergedData.filter((d) => d.data.find((c) => !!c))
    filledData.length === 0 && (mergedData.length = 0)

    // add flat chart lines for groupings not covered in the data
    const emptyDataArray: number[] = []
    while (emptyDataArray.length < arrayLength) {
      emptyDataArray.push(0)
    }
    const emptyGroups = groupings.filter(group => mergedData.find(m => m.name === group.name) === undefined)
    const emptyWidgetGroups = emptyGroups.filter(group => (widgetConstructor.grouping || []).find(g => g === group.name) !== undefined)
    for (const info of emptyWidgetGroups) {
      mergedData.push({ name: info.name, data: emptyDataArray })
      info?.color && chartColors.push(info.color)
    }

    return { data: mergedData, colors: chartColors }
  }

  const getWidgetPassings = (widgetConstructor: Widgets.Constructor) => {
    const currentPassingsInStats = data?.entries || []

    let passingsOld: number = 0
    let passingsNew: number = 0

    for (const stat of currentPassingsInStats) {
      const currentDate = DateTime.fromISO(stat.time)
      const selectedGrouping = !!widgetConstructor.grouping
        ? (!stat.classified && !!widgetConstructor.grouping.find((g) => g === 'Andre')) ||
          !!widgetConstructor.grouping.find((g) => g === stat.classified)
        : true
      const selectedFacility = !!widgetConstructor.facilities
        ? !!widgetConstructor.facilities.find((f) => f === stat.facility.name)
        : true
      selectedGrouping && selectedFacility && currentDate < getMiddleDate() && (passingsOld += stat.count)
      selectedGrouping && selectedFacility && currentDate >= getMiddleDate() && (passingsNew += stat.count)
    }

    let direction: 'up' | 'down' = 'up'
    let valueDifference = `${passingsNew - passingsOld}`

    if (valueDifference[0] === '-') {
      valueDifference = valueDifference.substr(1)
      direction = 'down'
    }

    const percentDifference =
      passingsOld !== 0 ? Math.round((+valueDifference / passingsOld) * 100) : +valueDifference !== 0 ? 100 : 0

    return {
      direction: direction,
      value: passingsNew,
      change: !isNaN(percentDifference) ? percentDifference : 0,
    }
  }

  useEffect(() => {
    if (fetchedData) {
      const newTrendData = getWidgetPassings(widgetConstructor)
      const newChartData = getPassingsChart(widgetConstructor)

      setTrendData(newTrendData)
      setChartData(newChartData)
    }
  }, [fetchedData, widgetConstructor]) // eslint-disable-line

  return chartData && widgetConstructor.variant === 'linechart' ? (
    <LineChartWidget
      title={widgetConstructor.name || t('components.dashboard.labels.passings')}
      description={`${
        !widgetConstructor.grouping
          ? t('components.dashboard.labels.allAvailableGroupings')
          : widgetConstructor.grouping && widgetConstructor.grouping?.length > 3
          ? `${widgetConstructor.grouping?.length} ${t('components.dashboard.labels.groupings')}`
          : widgetConstructor.grouping?.map((g, i) => `${i !== 0 ? ', ' : ''}${g}`).join('')
      } - ${
        !widgetConstructor.facilities
          ? t('components.dashboard.labels.allFacilities')
          : widgetConstructor.facilities && widgetConstructor.facilities?.length > 3
          ? `${widgetConstructor.facilities?.length} ${t('security.labels.facilities').toLowerCase()}`
          : widgetConstructor.facilities?.map((f, i) => `${i !== 0 ? ', ' : ''}${f}`).join('')
      } - ${TimePeriod.getFilterText(t('components.dashboard.labels.last1Day'), timeFrom, timeTo, widgetConstructor.currentPeriod)}`}
      data={chartData.data}
      xAxisLabels={TimePeriod.getChartLabels('last 24 hours', isSM)}
      dataLabel={'passing'}
      icon={<PassingsIcon />}
      onDetails={onDetails}
      detailsDisabled={chartData.data.filter((cd) => !!cd.data.find((d) => d !== 0)).length === 0}
      editing={editing}
      onRemove={onRemove}
      onSettings={onSettings}
      chartColors={chartData.colors}
    />
  ) : trendData && chartData && widgetConstructor.variant === 'trend' ? (
    <TrendWidget
      title={widgetConstructor.name || t('components.dashboard.labels.passings')}
      description={`${
        !widgetConstructor.grouping
          ? t('components.dashboard.labels.allAvailableGroupings')
          : widgetConstructor.grouping && widgetConstructor.grouping?.length > 3
          ? `${widgetConstructor.grouping?.length} ${t('components.dashboard.labels.groupings')}`
          : widgetConstructor.grouping?.map((g, i) => `${i !== 0 ? ', ' : ''}${g}`).join('')
      } - ${
        !widgetConstructor.facilities
          ? t('components.dashboard.labels.allFacilities')
          : widgetConstructor.facilities && widgetConstructor.facilities?.length > 3
          ? `${widgetConstructor.facilities?.length} ${t('security.labels.facilities').toLowerCase()}`
          : widgetConstructor.facilities?.map((f, i) => `${i !== 0 ? ', ' : ''}${f}`).join('')
      } - ${TimePeriod.getFilterText(t('components.dashboard.labels.last1Day'), timeFrom, timeTo, widgetConstructor.currentPeriod)}`}
      type="arrow"
      direction={trendData.direction}
      value={trendData.value}
      percentage={trendData.change}
      icon={<PassingsIcon />}
      onDetails={onDetails}
      detailsDisabled={chartData.data.filter((cd) => !!cd.data.find((d) => d !== 0)).length === 0}
      editing={editing}
      onRemove={onRemove}
      onSettings={onSettings}
      positiveValues={widgetConstructor.positiveValue === 'down' ? 'down' : 'up'}
    />
  ) : widgetConstructor.variant === 'trend' ? (
    <SkeletonLoader.Trend />
  ) : (
    <SkeletonLoader.LineChart />
  )
}

export const passingsWidget = (props: IPassingsWidgetProps): IWidget[] => {
  const { widgetConstructor } = props
  return widgetConstructor.variant === 'linechart'
    ? [
        {
          isResizable: true,
          height: [5],
          width: [1, 2],
          position: widgetConstructor.position,
          id: widgetConstructor.id,
          component: <Widget {...props} />,
        },
      ]
    : [
        {
          height: [4, 3, 3, 3],
          position: widgetConstructor.position,
          id: widgetConstructor.id,
          isResizable: true,
          maxWidth: 'half',
          maxHeight: 4,
          component: <Widget {...props} />,
        },
      ]
}

export default passingsWidget
